import { useState } from 'react';
import useSWR from 'swr';
import { api, getUserToken } from '@utils/api';

/**
 * Hook to fetch VIP packages
 */
export const useVIPPackages = () => {
    const { data, error, isLoading } = useSWR(
        api('/api/vip/packages'),
        async (url) => {
            const response = await fetch(url);
            const result = await response.json();

            if (result.success) {
                return result.packages || [];
            }
            return [];
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 300000, // 5 minutes
        }
    );

    return {
        packages: data || [],
        isLoading,
        error,
    };
};

/**
 * Hook to create Saweria payment
 */
export const useCreatePayment = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const token = getUserToken();

    const createPayment = async (packageCode) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(api('/api/vip/checkout'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ package_code: packageCode }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                return { success: true, data: data.data };
            }

            setError(data.message || 'Payment creation failed');
            return { success: false, message: data.message };
        } catch (err) {
            setError(err.message || 'Payment creation failed');
            return { success: false, message: err.message };
        } finally {
            setLoading(false);
        }
    };

    return {
        createPayment,
        loading,
        error,
    };
};

/**
 * Hook to check payment status
 */
export const usePaymentStatus = (transactionId, enabled = false) => {
    const { data, error, isLoading, mutate } = useSWR(
        enabled && transactionId ? [api(`/api/payments/saweria/debug/${transactionId}`), transactionId] : null,
        async ([url]) => {
            const response = await fetch(url);
            const result = await response.json();
            return result;
        },
        {
            refreshInterval: 5000, // Poll every 5 seconds
            revalidateOnFocus: true,
        }
    );

    return {
        paymentData: data || null,
        isLoading,
        error,
        mutate,
    };
};

export default {
    useVIPPackages,
    useCreatePayment,
    usePaymentStatus,
};
